import { useEffect, useMemo, useState } from 'react';

import { SELECTOR_MODE } from '~constants';
import { ParticipantModeHelper } from '~helpers';
import { ConsumerCategory, Product, Timeslot } from '~types/appsync.types';
import { Participant } from '~types/participant';
import { getIntConfigKeyValue, parseParticipantsFromUrl, setParticipantsInUrl } from '~utils';

import { useConfig } from '../useConfig/useConfig';

export interface UseParticipantSelectorOptions {
  sortedConsumerCategories: ConsumerCategory[];
  selectedProduct?: Product;
  syncUrl?: boolean;
  /** Pre-fill participants from HTML attribute. Same format as URL params: `p[1][age]=30&p[2][conscat]=CHILD`. Used as fallback when no URL param is present. */
  initialParticipants?: string;
}

export interface UseParticipantSelectorResult {
  participants: Participant[];
  selectedTimeslot: Timeslot | undefined;
  participantsLimit: number | undefined;
  participantMode: SELECTOR_MODE;
  handleParticipantsChange: (newParticipants: Participant[]) => void;
  handleTimeslotSelected: (slot: Timeslot | undefined) => void;
}

function updateParticipants(
  newParticipants: Participant[],
  setParticipants: (value: React.SetStateAction<Participant[]>) => void,
) {
  setParticipants(
    newParticipants.reduce((acc, newParticipant) => {
      const participantIds = acc.map((participant) => participant.id);
      if (!participantIds.includes(newParticipant.id)) {
        acc.push(newParticipant);
      }
      return acc;
    }, [] as Participant[]),
  );
}

const useParticipantSelector = ({
  sortedConsumerCategories,
  syncUrl = true,
  initialParticipants,
}: UseParticipantSelectorOptions): UseParticipantSelectorResult => {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [selectedTimeslot, setSelectedTimeslot] = useState<Timeslot | undefined>(undefined);

  const [config] = useConfig();
  const baseParticipantsLimit = getIntConfigKeyValue(config, 'cart.max_overall_quantity');

  const participantsLimit = useMemo(() => {
    if (selectedTimeslot) {
      const limits: number[] = [];

      if (typeof baseParticipantsLimit === 'number') {
        limits.push(baseParticipantsLimit);
      }

      if (!selectedTimeslot.quotaUnlimited) {
        limits.push(selectedTimeslot.quotaAvailable);
      }

      if (selectedTimeslot.limitQuotaOrderVel > 0) {
        limits.push(selectedTimeslot.limitQuotaOrderVel);
      }

      return limits.length > 0 ? Math.min(...limits) : baseParticipantsLimit;
    }
    return baseParticipantsLimit;
  }, [selectedTimeslot, baseParticipantsLimit]);

  // Reassign consumerCategories when product changes
  useEffect(() => {
    if (!sortedConsumerCategories?.length) return;

    let hasChanges = false;
    const updatedParticipants = participants.map((participant) => {
      const currentCategoryFits =
        participant.consumerCategory &&
        participant.consumerCategory.ageMin <= participant.age &&
        participant.consumerCategory.ageMax >= participant.age &&
        sortedConsumerCategories.some(
          (cc) => cc.shortname === participant.consumerCategory.shortname,
        );

      if (currentCategoryFits) return participant;

      const matchedCategory = sortedConsumerCategories.find(
        (cc) => cc.ageMin <= participant.age && cc.ageMax >= participant.age && cc.visible === true,
      );

      if (matchedCategory && matchedCategory.shortname !== participant.consumerCategory.shortname) {
        hasChanges = true;
      }

      return {
        ...participant,
        consumerCategory: matchedCategory ?? participant.consumerCategory,
      };
    });

    if (hasChanges) {
      updateParticipants(updatedParticipants, setParticipants);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedConsumerCategories, participants.length]);

  // Parse participants from URL or initial attribute
  useEffect(() => {
    if (!syncUrl && !initialParticipants) return;

    const consumerCategories = sortedConsumerCategories;
    if (!consumerCategories?.length) return;

    const urlParticipants = syncUrl
      ? parseParticipantsFromUrl(new URL(globalThis.location.href).searchParams, consumerCategories)
      : [];

    const initialSearchParams = initialParticipants
      ? new URLSearchParams(initialParticipants)
      : null;
    const initialParsedParticipants = initialSearchParams
      ? parseParticipantsFromUrl(initialSearchParams, consumerCategories)
      : [];

    // HTML attribute takes priority over URL param
    const newParticipants =
      initialParsedParticipants.length > 0 ? initialParsedParticipants : urlParticipants;

    if (newParticipants.length !== participants.length) {
      updateParticipants(newParticipants, setParticipants);
    }
  }, [sortedConsumerCategories, participants.length, syncUrl, initialParticipants]);

  // Timeslot → participants sync
  useEffect(() => {
    setParticipants((prev) => {
      if (!prev || prev.length === 0) return prev;
      if (selectedTimeslot?.externalId) {
        return prev.map((p) => ({ ...p, timeslotExternalId: selectedTimeslot.externalId }));
      }
      return prev.map((p) => ({ ...p, timeslotExternalId: undefined }));
    });
  }, [selectedTimeslot]);

  const handleParticipantsChange = (newParticipants: Participant[]) => {
    if (syncUrl) {
      setParticipantsInUrl(newParticipants, ParticipantModeHelper.getMode());
    }
    const timeslotExternalId = selectedTimeslot?.externalId;
    const participantsWithBooking = timeslotExternalId
      ? newParticipants.map((p) => (p.timeslotExternalId ? p : { ...p, timeslotExternalId }))
      : newParticipants;

    updateParticipants(participantsWithBooking, setParticipants);
  };

  const handleTimeslotSelected = (slot: Timeslot | undefined) => {
    setSelectedTimeslot(slot);
  };

  return {
    handleParticipantsChange,
    handleTimeslotSelected,
    participantMode: ParticipantModeHelper.getMode(),
    participants,
    participantsLimit,
    selectedTimeslot,
  };
};

export default useParticipantSelector;
